// react
import React from "react";
import {NavigateFunction} from "react-router-dom";

// local
import {
	Client,
	FrameworkDetails,
	StudentSummary,
	TeacherSummary,
	User,
	timestampToDuration,
	timestampToStrDate,
} from "../../types";
import {
	Loading,
} from "../../components";
import {FiUsers} from "react-icons/fi";

/**
 * FrameworkSection
 */

interface SectionProps {
	user: User,
	client: Client
	navigate: NavigateFunction
}
interface SectionState {
	loaded: boolean,
	sFramework: FrameworkDetails[],
	teachers: TeacherSummary[],
	teacherId: number,
	students: StudentSummary[],
	studentId: number,
	sCollapsed: Set<string>,
}

export class FrameworkSection extends React.Component<SectionProps, SectionState> {

	constructor(props: SectionProps) {
		super(props);

		// initial state
		this.state = {
			loaded: false,
			sFramework: [],
			teachers: [],
			teacherId: 10515,
			students: [],
			studentId: 0,
			sCollapsed: new Set<string>(),
		}

		// Register the callbacks.
	}

	componentDidMount = async () => {
		// setup
		const {client} = this.props;
		const {teacherId} = this.state;

		const teachers = await client.fetchTeachers(client.name, teacherId);
		const students = await client.fetchStudents(client.name, teacherId);

		this.setState({teachers, students, loaded: true});
	}

	onSelectStudent = async (studentId: number)=> {
		// setup
		const {client} = this.props;
		const results = await client.fetchStudentDetails(client.name, studentId, 0, 0);
		const sFramework: FrameworkDetails[] = results.framework || [];
		const sCollapsed = new Set<string>();
		this.setState({sFramework, studentId, sCollapsed});
	}

	onSelectTeacher = async (teacherId: number)=> {
		// setup
		const {client} = this.props;
		const teachers = await client.fetchTeachers(client.name, teacherId);
		const students = await client.fetchStudents(client.name, teacherId);
		this.setState({teachers, teacherId, students});
	}

	onReloadList = async () => {
	}

	toggleStudentCollapsed = (key: string) => {
		this.setState(prev => {
			const sCollapsed = new Set(prev.sCollapsed);
			if (sCollapsed.has(key)) {
				sCollapsed.delete(key);
			} else {
				sCollapsed.add(key);
			}
			return {sCollapsed};
		});
	}

	/*****************************************************************************************************************
	 * RENDER FUNCTIONS are in the section below.
	 *****************************************************************************************************************/

	render() {
		// setup
		const {loaded} = this.state;

		// render
		if (!loaded) {
			return <Loading />
		}
		return (
			<div>
				<div className='teachers'>
					{this.renderManagersHeader()}
					{this.renderManagersTable()}
					<hr/>
				</div>
				<div className='students'>
					{this.renderStudentsHeader()}
					{this.renderStudentsTable()}
					<hr/>
				</div>
				<div className='sFramework'>
					{this.renderDetailsHeader()}
					{this.renderDetailsTable()}
					<hr/>
				</div>
			</div>
		)
	}

	renderManagersHeader() {
		// render
		return <div className='section-header'>
			<FiUsers className="section-icon" size={28}/>
			Managers
		</div>
	}

	renderManagersTable() {
		// setup
		const {teachers, loaded} = this.state;
		const border = '1px solid #ddd';
		const padding = "padding: '4px 8px'";
		if (!loaded || teachers.length === 0) {
			return <div className="row" />;
		}

		const vHeader = <tr style={{borderBottom: '2px solid #ddd', fontWeight: 'bold'}}>
				<th style={{textAlign: 'left', padding: '4px 8px', width: 25, border}}></th>
				<th style={{textAlign: 'left', padding: '4px 8px', width: 75, border}}>#</th>
				<th style={{textAlign: 'left', padding: '4px 8px', width: 200, border}}>Email</th>
				<th style={{textAlign: 'left', padding: '4px 8px', border}}>Name</th>
				<th style={{textAlign: 'left', padding: '4px 8px', width: 100, border}}>Students</th>
			</tr>

		const vBody= teachers.map(s => {
			const trstyle = {
				cursor: 'pointer',
				backgroundColor: s.level ? 'transparent' : '#e8e8e8',
				fontWeight: s.level ? 'normal' : 'bold'
			};

			const color = (s.level == 0) ? 'black' : (s.level == 1) ? 'green' : 'gray';
			const level = (s.level == 0) ? '▶' : s.level;
			return <tr key={s.id} style={trstyle} onClick={() => this.onSelectTeacher(s.id)}>
				<td style={{width: 25, textAlign: 'center', border, padding, color: color}}>{level}</td>
				<td style={{width: 75, border, padding}}>{s.id}</td>
				<td style={{width: 200, border, padding}}>{s.email}</td>
				<td style={{border, padding}}>{s.lastname + ', ' + s.firstname}</td>
				<td style={{width: 100, border, padding}}>{s.students}</td>
			</tr>
		});

		return (
			<div style={{padding: '12px 16px', textAlign: 'left'}}>
				<table style={{width: '100%', borderCollapse: 'collapse', border}}>
					<thead>
						{vHeader}
					</thead>
				</table>
				<div style={{maxHeight: 250, overflowY: 'auto'}}>
					<table style={{width: '100%', borderCollapse: 'collapse', border, fontSize: '12px'}}>
						<tbody>
							{vBody}
						</tbody>
					</table>
				</div>
			</div>
		);
	}

	renderStudentsHeader() {
		// setup
		const {teachers, teacherId} = this.state;
		const teacher = teachers.find(t => t.id == teacherId);
		const title = teacher ? `Students of ${teacher.lastname}, ${teacher.firstname}` : '';

		// render
		return <div className='section-header'>
			<FiUsers className="section-icon" size={28}/>
			{title}
		</div>
	}

	renderStudentsTable() {
		// setup
		const {students, studentId, loaded} = this.state;
		const padding = "padding: '4px 8px'";

		if (!loaded || students.length === 0) {
			return <div className="row" />;
		}

		const border = '1px solid #ddd';
		const vHeader = <tr style={{borderBottom: '2px solid #ddd', fontWeight: 'bold'}}>
			<th style={{textAlign: 'left', padding, width: 75, border}}>#</th>
			<th style={{textAlign: 'left', padding, width: 200, border}}>Email</th>
			<th style={{textAlign: 'left', padding, border}}>Name</th>
			<th style={{textAlign: 'center', padding, width: 100, border}}>Enrolled</th>
			<th style={{textAlign: 'center', padding, width: 100, border}}>Started</th>
			<th style={{textAlign: 'center', padding, width: 100, border}}>Completed</th>
			<th style={{textAlign: 'center', padding, width: 100, border}}>Ave Score</th>
			<th style={{textAlign: 'center', padding, width: 100, border}}>HH:MM</th>
		</tr>;

		const vBody = students.map(s => {
			let enrolled = s.enrolled ? s.enrolled : '-';
			let started = s.enrolled ? s.started + ' (' + Math.ceil(s.started * 100 / s.enrolled) + '%)' : '';
			let completed = s.enrolled ? s.completed + ' (' + Math.ceil(s.completed * 100 / s.enrolled) + '%)': '';
			let score = s.completed ? s.score + '%' : '';
			const trstyle = {
				cursor: 'pointer',
				backgroundColor: s.id === studentId ? '#e8e8e8' : 'transparent'
			};
			return <tr key={s.id} style={trstyle} onClick={() => this.onSelectStudent(s.id)}>
				<td style={{padding, width: 75, border}}>{s.id}</td>
				<td style={{padding, width: 200, border}}>{s.email}</td>
				<td style={{padding, border}}>{s.lastname + ', ' + s.firstname}</td>
				<td style={{textAlign: 'center', padding, width: 100, border}}>{s.enrolled}</td>
				<td style={{textAlign: 'center', padding, width: 100, border}}>{started}</td>
				<td style={{textAlign: 'center', padding, width: 100, border}}>{completed}</td>
				<td style={{textAlign: 'center', padding, width: 100, border}}>{score}</td>
				<td style={{textAlign: 'center', padding, width: 100, border}}>{timestampToDuration(s.time)}</td>
			</tr>
		});

		return (
			<div style={{padding: '12px 16px', textAlign: 'left'}}>
				<table style={{width: '100%', borderCollapse: 'collapse', border}}>
					<thead>
					{vHeader}
					</thead>
				</table>
				<div style={{maxHeight: 250, overflowY: 'auto'}}>
				<table style={{width: '100%', borderCollapse: 'collapse', border, fontSize: '12px'}}>
					<tbody>
					{vBody}
					</tbody>
				</table>
				</div>
			</div>
		);
	}

	renderDetailsHeader() {
		// setup
		const {students, studentId} = this.state;
		const student = students.find(s => s.id == studentId);

		// render
		const identifier = student ? `${student.lastname}, ${student.firstname} — ${student.email}` : '';
		return <div className='section-header'>
			<FiUsers className="section-icon" size={28}/>
			Student Details
			<div style={{fontSize: '0.9em', color: '#555', marginTop: 4}}>
				{identifier}
			</div>
		</div>
	}

	renderDetailsTable() {
		// setup
		const {sFramework, loaded, sCollapsed: collapsed} = this.state;

		// Make sure the sFramework is loaded.
		if (!loaded || sFramework.length === 0) {
			return <div className="row" />;
		}

		// Build a map using composite keys (level:id) since id is only unique per level.
		const itemKey = (i: FrameworkDetails) => `${i.level}:${i.id}`;
		const parentKey = (i: FrameworkDetails) => `${i.level - 1}:${i.parentid}`;
		const childrenMap = new Map<string, FrameworkDetails[]>();
		const roots: FrameworkDetails[] = [];

		sFramework.filter(item => item.type !== 'scorm').forEach(item => {
			if (item.parentid === 0) {
				roots.push(item);
			} else {
				const key = parentKey(item);
				const siblings = childrenMap.get(key) || [];
				siblings.push(item);
				childrenMap.set(key, siblings);
			}
		});

		// Recursively render tree rows.
		const visited = new Set<string>();
		const rows: React.ReactNode[] = [];

		const renderNode = (item: FrameworkDetails, depth: number) => {
			const key = itemKey(item);
			if (visited.has(key)) return;
			visited.add(key);

			const children = childrenMap.get(key) || [];
			const hasChildren = children.length > 0;
			const isCollapsed = collapsed.has(key);
			const indent = depth * 20;

			let enrolled; let started; let completed; let score; let duration; let style;
			if (item.type == 'course') {
				style = {border: '1px solid #ddd', fontWeight: 'bold'};
			} else if (item.type == 'section') {
				style = {border: '1px solid #ddd', color: 'green'};
			} else {
				style = {border: '1px solid #ddd'};
			}
			if (item.type != 'course' && item.type != 'section') {
				enrolled = item.enrolled ? timestampToStrDate(item.enrolled, false) : '-';
				started = timestampToStrDate(item.started, false);
				completed = timestampToStrDate(item.completed, false);
				duration = item.enrolled ? timestampToDuration(item.time) : '';
				score = item.enrolled ? item.score + '%' : '';
			} else if (item.enrolled) {
				enrolled = item.enrolled;
				started = item.started + ' (' + Math.ceil(item.started * 100 / item.enrolled) + '%)';
				completed = item.completed + ' (' + Math.ceil(item.completed * 100 / item.enrolled) + ')%';
				duration = timestampToDuration(item.time);
				score = item.score + '%';
			} else {
				enrolled = '-';
				started = completed  = duration = score = '';
			}
			const link = (item.type == 'lesson') ? <a href={item.url} target='_blank' rel="noreferrer">module</a> : null;

			rows.push(
				<tr key={key} style={{cursor: hasChildren ? 'pointer' : 'default', fontSize: '12px', textAlign: 'center'}}
					onClick={hasChildren ? () => this.toggleStudentCollapsed(key) : undefined}
				>
					<td style={{paddingLeft: indent, whiteSpace: 'nowrap', border: '1px solid #ddd', textAlign: 'left'}}>
						{hasChildren && <span style={{marginRight: 4}}>{isCollapsed ? '▶' : '▼'}</span>}
						<span style={{fontWeight: depth === 0 ? 'bold' : 'normal'}}>{item.title}</span>
					</td>
					<td style={style}>{enrolled}</td>
					<td style={style}>{started}</td>
					<td style={style}>{completed}</td>
					<td style={style}>{score}</td>
					<td style={style}>{duration}</td>
					<td style={style}>{link}</td>
				</tr>
			);

			if (!isCollapsed) {
				children.forEach(child => renderNode(child, depth + 1));
			}
		};

		roots.forEach(root => renderNode(root, 0));

		// render
		const border = '1px solid #ddd';
		return (
			<div style={{padding: '12px 16px', textAlign: 'left'}}>
				<table style={{width: '100%', borderCollapse: 'collapse', border}}>
					<thead style={{textAlign: 'center'}}>
						<tr style={{borderBottom: '2px solid #ddd', fontWeight: 'bold'}}>
							<th style={{textAlign: 'left', padding: '4px 8px', width: '50%', border}}>Title</th>
							<th style={{padding: '4px 8px', border}}>Enrolled</th>
							<th style={{padding: '4px 8px', border}}>Started</th>
							<th style={{padding: '4px 8px', border}}>Completed</th>
							<th style={{padding: '4px 8px', border}}>Score</th>
							<th style={{padding: '4px 8px', border}}>HH:MM</th>
							<th style={{padding: '4px 8px', border}}>Link</th>
						</tr>
					</thead>
					<tbody>
						{rows}
					</tbody>
				</table>
			</div>
		);
	}

}

